import { z } from "zod";
import { defineEndpoint } from "./endpoint.js";
import { publicImageSchema, storefrontHostSchema } from "./storefront.js";
import { themeOptionsSchema } from "./storefront-theme-kits.js";
/**
 * The online store's look (B-79, SF-11): a base theme (B-85 adds the business themes), brand colours, corners, fonts,
 * announcement bar, menus, social links and policy texts. Edited as a draft, previewed by a signed link, then
 * published. Colours must keep text readable (WCAG AA 4.5:1 against white).
 */
const tag = { tag: "storefront" };
const hex = z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .transform((v) => v.toLowerCase());
const href = z
    .string()
    .trim()
    .max(300)
    .regex(/^(\/[^\s]*|https:\/\/[^\s]+|tel:\+?\d+|mailto:[^\s]+)$/);
/** B-85: the ready-made business themes (11 approved by the owner 2026-10-30; `books` added in DG-08). */
export const THEME_KEYS = [
    "fashion_women",
    "fashion_men",
    "cosmetics",
    "gadgets",
    "grocery",
    "home_kitchen",
    "kids_baby",
    "jewellery",
    "home_food",
    "books",
    "single_product",
    "minimal",
];
/**
 * PL-07 (docs/specs/plan-catalogue.md): themes that need the plan's `feature.premium_themes`. A shop already on one
 * keeps it; only choosing one anew is checked.
 */
export const PREMIUM_THEMES = [
    "fashion_women",
    "cosmetics",
    "gadgets",
    "jewellery",
    "books",
    "single_product",
];
export const isPremiumTheme = (key) => PREMIUM_THEMES.includes(key);
export const THEME_RADII = ["none", "small", "medium", "large"];
export const THEME_FONTS = ["inter", "serif", "rounded"];
export const POLICY_KINDS = ["returns", "delivery", "privacy", "terms"];
/** ST-12: a floating chat button on every page, opening the shop's WhatsApp or Messenger. */
export const FLOATING_CHAT = ["off", "whatsapp", "messenger"];
export const menuItemSchema = z.object({ label: z.string().trim().min(1).max(40), href });
export const themeSchema = z.object({
    base: z.string().min(1).max(40).default("minimal"),
    colors: z
        .object({
        primary: hex.default("#047857"),
        accent: hex.default("#f59e0b"),
    })
        .default({ primary: "#047857", accent: "#f59e0b" }),
    /**
     * Themes v2 (ST-01): a curated colour set of the base theme, or `custom` (the `colors` above). What a theme offers
     * is in `storefront-theme-kits.ts`; the API rejects anything else.
     */
    palette: z.string().trim().min(1).max(40).default("custom"),
    /** Font pairing of the base theme (`signature` = the theme's own fonts). */
    fontPairing: z.string().trim().min(1).max(40).default("signature"),
    /** Layout and behaviour choices; missing keys use the theme's defaults. */
    options: themeOptionsSchema.default({}),
    radius: z.enum(THEME_RADII).default("large"),
    headingFont: z.enum(THEME_FONTS).default("inter"),
    announcement: z
        .object({
        enabled: z.boolean(),
        text: z.string().trim().max(120),
        href: href.or(z.literal("")),
        /** ST-05: up to two more messages, shown in turn or as a ticker (theme option `announcementStyle`). */
        more: z.array(z.string().trim().min(1).max(120)).max(2).default([]),
    })
        .default({ enabled: false, text: "", href: "", more: [] }),
    /** ST-05: what the search overlay offers before the shopper types ("Eid collection", "honey"). */
    trendingSearches: z.array(z.string().trim().min(1).max(40)).max(8).default([]),
    headerMenu: z.array(menuItemSchema).max(8).default([]),
    footerMenu: z.array(menuItemSchema).max(12).default([]),
    social: z
        .object({
        facebook: z.url().max(200).or(z.literal("")),
        instagram: z.url().max(200).or(z.literal("")),
        youtube: z.url().max(200).or(z.literal("")),
        tiktok: z.url().max(200).or(z.literal("")),
        whatsapp: z.string().trim().max(20),
    })
        .default({ facebook: "", instagram: "", youtube: "", tiktok: "", whatsapp: "" }),
    /** ST-12: chat button; needs the WhatsApp number or the Facebook page in `social` (checked on save). */
    floatingChat: z.enum(FLOATING_CHAT).default("off"),
    /** ST-12: short promises the merchant stands behind ("Original products", "7-day exchange"), up to 4. */
    trustBadges: z.array(z.string().trim().min(1).max(40)).max(4).default([]),
    /** Plain text with blank lines between paragraphs (shown as text, never as HTML). */
    policies: z
        .object({
        returns: z.string().max(10_000),
        delivery: z.string().max(10_000),
        privacy: z.string().max(10_000),
        terms: z.string().max(10_000),
    })
        .default({ returns: "", delivery: "", privacy: "", terms: "" }),
});
export const storefrontThemeSchema = z.object({
    draft: themeSchema,
    published: themeSchema.nullable(),
    publishedAt: z.iso.datetime().nullable(),
    /** Opens the shop with the draft for 24 hours (no sign-in). */
    previewUrl: z.string(),
    version: z.int(),
});
export const getStorefrontTheme = defineEndpoint({
    ...tag,
    name: "getStorefrontTheme",
    method: "GET",
    path: "/v1/storefront-theme",
    access: { permission: "storefront.view" },
    summary: "The shop's theme: the draft, what is live, and a preview link",
    status: 200,
    response: storefrontThemeSchema,
});
export const saveStorefrontTheme = defineEndpoint({
    ...tag,
    name: "saveStorefrontTheme",
    method: "PUT",
    path: "/v1/storefront-theme",
    access: { permission: "storefront.manage" },
    summary: "Save the draft (shoppers do not see it until it is published)",
    body: z.object({ draft: themeSchema, version: z.int().min(0) }).strict(),
    status: 200,
    response: storefrontThemeSchema,
    errors: ["CONFLICT"],
});
export const publishStorefrontTheme = defineEndpoint({
    ...tag,
    name: "publishStorefrontTheme",
    method: "POST",
    path: "/v1/storefront-theme/publish",
    access: { permission: "storefront.manage" },
    summary: "Make the draft live",
    body: z.object({ version: z.int().min(1) }).strict(),
    status: 200,
    response: storefrontThemeSchema,
    errors: ["CONFLICT"],
});
export const shopThemeSchema = themeSchema.extend({
    logo: publicImageSchema.nullable(),
    /** True when this is the draft seen through a preview link. */
    preview: z.boolean(),
});
export const getShopTheme = defineEndpoint({
    ...tag,
    name: "getShopTheme",
    method: "GET",
    path: "/v1/storefront/theme",
    access: { public: true },
    summary: "The live theme of a shop (or its draft with a valid preview token)",
    query: z.object({ host: storefrontHostSchema, preview: z.string().max(200).optional() }),
    status: 200,
    response: shopThemeSchema,
});
//# sourceMappingURL=storefront-theme.js.map